import { MinimapRenderer, type ColorTokenResolver } from "@/lib/engine/components/minimap-renderer";
import { LocalTransport } from "@/lib/track/local-transport";
import { TrackManager } from "@/lib/track/track-manager";
import { Lane } from "@/lib/track/lane";
import { Container, FederatedPointerEvent } from "pixi.js";
import { ViewNavigator } from "../view-navigator";
import { EMPTY_LAYOUT, type SequenceLayout } from "../sequence-layout";

/** Cadence de reconstruction du rail pour une note tenue (voir `lastLiveRebuildMs`). */
const LIVE_REBUILD_INTERVAL_MS = 150;

/**
 * Largeur du rail de minimap sur écran large, en px CSS. Sert aussi de valeur
 * d'amorçage à l'observable `Studio.minimapWidth` (rien à mesurer avant le
 * premier layout Pixi).
 */
export const MINIMAP_WIDTH = 60;

/**
 * Largeur du rail sur canvas étroit. À 620 px de large, 60 px représentaient
 * 9 % de l'écran pris au clavier ; 44 px en rendent une partie sans que le
 * rail cesse d'être lisible. Jamais 0 : la minimap est la seule carte du
 * morceau, la masquer supprimerait la navigation, pas un ornement.
 */
const MINIMAP_WIDTH_COMPACT = 44;

/**
 * Largeur de **canvas** au-dessous de laquelle le rail passe en version
 * étroite. Attention : c'est bien le canvas, pas le viewport — ouvrir la
 * sidebar (200 px) rétrécit le premier sans toucher au second.
 *
 * D'où la valeur : le canvas le plus étroit qu'on puisse obtenir à partir de
 * 1024 px de viewport, c'est 1024 − 200 = 824 px, sidebar ouverte. Rester en
 * dessous garantit qu'aucun poste ≥ 1024 px ne voie le rail changer de largeur
 * en repliant un panneau. Au-dessus (900 px, la première valeur retenue), il
 * sautait de 60 à 44 px entre 1024 et 1124 px — un mouvement que rien dans le
 * geste de l'utilisateur ne justifiait.
 *
 * Côté étroit on garde l'essentiel : un iPhone SE couché (667 px) passe bien
 * en rail étroit, et à 844 px les 60 px ne coûtent plus que 7 % de la largeur.
 */
const MINIMAP_COMPACT_BELOW = 800;

/**
 * Largeur du rail pour une largeur de canvas donnée. Seule autorité : la vue
 * la publie à chaque layout (`Studio.minimapWidth`) et les overlays React la
 * lisent via `useMinimapWidth()`, au lieu de recopier la constante.
 */
export function minimapWidthFor(canvasWidth: number): number {
  return canvasWidth < MINIMAP_COMPACT_BELOW ? MINIMAP_WIDTH_COMPACT : MINIMAP_WIDTH;
}

/**
 * MinimapComponent: high-level wiring for the minimap.
 *
 * Responsibilities:
 * - Creates and manages the MinimapRenderer
 * - Syncs lanes from TrackManager (same pattern as PianoRollComponent)
 * - Handles click/drag interaction for seek
 * - Delegates update() calls each frame to the renderer
 *
 * Does NOT:
 * - Create its own Viewport/Layout
 * - Know about the Engine or Application (StudioView handles that)
 * - Manage audio or scheduling
 */
export class MinimapComponent {
  private renderer?: MinimapRenderer;
  private unsubscribeTrackManager?: () => void;
  private isDragging = false;

  /** Unsubscribe handles for per-track onChange listeners */
  private trackUnsubscribes: (() => void)[] = [];
  /** Unsubscribe handles for per-lane note event listeners */
  private laneUnsubscribes: (() => void)[] = [];
  /** Set when notes are added/removed, checked each frame in update() */
  private notesDirty = false;
  /** Number of currently held live notes, rebuild every frame while > 0 */
  private liveNotesActive = 0;
  /**
   * Dernière reconstruction due à une note tenue seule.
   *
   * Une note tenue ne change que son extrémité, et sur un rail de 60 px de
   * large elle ne bouge visiblement que tous les quelques dixièmes de seconde.
   * La reconstruire à chaque image, c'est refaire tout le morceau (toutes les
   * notes de toutes les lanes, plus la grille) 60 fois par seconde pendant tout
   * le temps où un doigt est posé.
   */
  private lastLiveRebuildMs = 0;
  /**
   * Largeur courante du rail. Amorcée à la valeur large, puis fixée par
   * `resize()` à chaque layout — la zone cliquable doit suivre le rail dessiné.
   */
  private width = MINIMAP_WIDTH;

  constructor(
    private stage: Container,
    private localTransport: LocalTransport,
    private trackManager: TrackManager,
    private viewNavigator: ViewNavigator,
    private getColorToken: ColorTokenResolver,
    /** La séquence de morceaux vue depuis le rouleau ; voir `MinimapRenderer`. */
    private sequence: SequenceLayout = EMPTY_LAYOUT,
  ) {}

  build(): void {
    this.renderer = new MinimapRenderer(
      this.stage,
      this.localTransport,
      this.getColorToken,
      { width: this.width },
      this.sequence,
    );

    this.renderer.build();

    // Sync lanes from all tracks
    this.syncLanes();

    // Listen for track changes
    this.unsubscribeTrackManager = this.trackManager.onChange(() => {
      this.syncLanes();
    });

    // Make the stage interactive for click-to-seek
    this.stage.eventMode = "static";
    this.stage.cursor = "pointer";
    this.stage.hitArea = { contains: (x: number, y: number) => {
      return x >= 0 && x <= this.width && y >= 0 && y <= (this.renderer?.getHeight() ?? 400);
    }};

    this.stage.on("pointerdown", this.onPointerDown);
    this.stage.on("pointermove", this.onPointerMove);
    this.stage.on("pointerup", this.onPointerUp);
    this.stage.on("pointerupoutside", this.onPointerUp);
  }

  /**
   * Called every frame by the Pixi ticker (via StudioView).
   * Only rebuilds note graphics when notes have actually changed or
   * live notes are being held (their visual extent changes each frame).
   */
  update(): void {
    const currentTick = this.viewNavigator.getEffectiveTick();

    if (this.notesDirty) {
      this.notesDirty = false;
      this.lastLiveRebuildMs = performance.now();
      this.renderer?.rebuildNotes(currentTick);
    } else if (this.liveNotesActive > 0 || this.renderer?.notesAheadOfHead === true) {
      // Une note tenue s'allonge à chaque image, et une note captée au présent
      // de la séance ne se montre qu'une fois atteinte par la tête : dans les
      // deux cas, rien ne marque les notes comme changées.
      const now = performance.now();

      if (now - this.lastLiveRebuildMs >= LIVE_REBUILD_INTERVAL_MS) {
        this.lastLiveRebuildMs = now;
        this.renderer?.rebuildNotes(currentTick);
      }
    }

    this.renderer?.update(currentTick);
  }

  /**
   * Marque les notes à reconstruire au prochain passage.
   *
   * Le rail ne se reconstruit que sur événement de piste — `noteOn`,
   * `noteOff`, `hide`/`show`. Vider une piste n'en émet aucun : sans ça, les
   * notes effacées resteraient dessinées dans le rail.
   */
  markNotesDirty(): void {
    this.notesDirty = true;
  }

  /**
   * Set the visible tick range for the viewport rectangle display.
   */
  setVisibleTickRange(range: number): void {
    this.renderer?.setVisibleTickRange(range);
  }

  /**
   * Redimensionne le rail. La largeur vient de {@link minimapWidthFor},
   * résolue par la vue qui doit la connaître avant de dimensionner le centre.
   */
  resize(width: number, height: number): void {
    this.width = width;
    this.renderer?.resize(width, height);
  }

  /** Largeur courante du rail. */
  get naturalWidth(): number {
    return this.width;
  }

  /** Natural height of the minimap */
  get naturalHeight(): number {
    return this.renderer?.getHeight() ?? 400;
  }

  // --- Interaction handlers ---

  private onPointerDown = (e: FederatedPointerEvent): void => {
    this.isDragging = true;
    this.viewNavigator.beginGesture();
    this.navigateToPointer(e);
  };

  private onPointerMove = (e: FederatedPointerEvent): void => {
    if (!this.isDragging) {
return;
}

    this.navigateToPointer(e);
  };

  private onPointerUp = (): void => {
    if (!this.isDragging) {
return;
}

    this.isDragging = false;
    // Minimap navigation commits immediately (no inertia)
    this.viewNavigator.endGesture(/* immediate */ true);
  };

  /**
   * Set the view offset so that the effective tick corresponds to the
   * Y position clicked on the minimap. This is an absolute positioning,
   * not a delta, so we replace the current offset entirely.
   */
  private navigateToPointer(e: FederatedPointerEvent): void {
    if (!this.renderer) {
return;
}

    const local = this.stage.toLocal(e.global);
    // Le rail répond sur l'axe global ; la vue, elle, se déplace en ticks du
    // morceau courant — négatifs pour un morceau d'avant, au-delà de sa durée
    // pour un morceau d'après. C'est le plancher du navigateur qui admet les
    // premiers, et la borne du transport qui change de morceau à l'arrivée.
    const targetTick = this.renderer.yToTick(local.y) - this.sequence.currentOffset;
    const transportTick = this.localTransport.currentTick();
    // Replace the offset so that effectiveTick = targetTick
    const newOffset = targetTick - transportTick;

    // We need to set the offset directly. Use updateOffset with the delta
    // from the current viewOffset.
    const delta = newOffset - this.viewNavigator.viewOffset;
    this.viewNavigator.updateOffset(delta);
  }

  // --- Lane syncing (same pattern as PianoRollComponent) ---

  private syncLanes(): void {
    // Unsubscribe previous listeners to prevent accumulation
    for (const unsub of this.trackUnsubscribes) {
unsub();
}

    this.trackUnsubscribes = [];
    for (const unsub of this.laneUnsubscribes) {
unsub();
}

    this.laneUnsubscribes = [];
    this.liveNotesActive = 0;

    const lanes: Lane[] = [];

    for (const track of this.trackManager.getTracks()) {
      for (const lane of track.getLanes()) {
        lanes.push(lane);
        this.subscribeLane(lane);
      }

      // Also listen for future lane additions within each track
      const unsub = track.onChange(() => {
        this.syncLanesImmediate();
      });
      this.trackUnsubscribes.push(unsub);
    }

    this.renderer?.setLanes(lanes);
  }

  private syncLanesImmediate(): void {
    for (const unsub of this.laneUnsubscribes) {
unsub();
}

    this.laneUnsubscribes = [];
    this.liveNotesActive = 0;

    const lanes: Lane[] = [];
    for (const track of this.trackManager.getTracks()) {
      for (const lane of track.getLanes()) {
        lanes.push(lane);
        this.subscribeLane(lane);
      }
    }

    this.renderer?.setLanes(lanes);
  }

  /** Subscribe to a lane's note events to mark the minimap as dirty. */
  private subscribeLane(lane: Lane): void {
    const unsub = lane.on((e) => {
      if (e.type === 'noteOn') {
        this.notesDirty = true;
        this.liveNotesActive++;
      } else if (e.type === 'noteOff') {
        this.notesDirty = true;
        this.liveNotesActive = Math.max(0, this.liveNotesActive - 1);
      } else if (e.type === 'hide' || e.type === 'show') {
        this.notesDirty = true;
      }
    });
    this.laneUnsubscribes.push(unsub);
  }

  async destroy(): Promise<void> {
    this.unsubscribeTrackManager?.();

    // Clean up per-track and per-lane listeners
    for (const unsub of this.trackUnsubscribes) {
unsub();
}

    this.trackUnsubscribes = [];
    for (const unsub of this.laneUnsubscribes) {
unsub();
}

    this.laneUnsubscribes = [];

    this.stage.off("pointerdown", this.onPointerDown);
    this.stage.off("pointermove", this.onPointerMove);
    this.stage.off("pointerup", this.onPointerUp);
    this.stage.off("pointerupoutside", this.onPointerUp);
    this.renderer?.destroy();
    this.renderer = undefined;
  }
}
